/**
 * Webtech HR Engine™ - Skills Cockpit (Strategic Dashboard)
 * Cockpit stratégique de pilotage des compétences
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Brain,
  Users,
  Target,
  AlertTriangle,
  TrendingUp,
  Award,
  Loader2,
  ChevronRight,
  BarChart3,
  Eye,
  Plus,
  Sparkles,
  Zap,
  Crown,
  CheckCircle2,
  AlertCircle,
  Activity,
  Gauge,
  BookOpen,
  GraduationCap,
  RefreshCw,
  Building2,
  UserCheck,
  ArrowRight,
  Info,
  FileText,
  Clock,
  Star,
  PieChart
} from 'lucide-react';

// Level configuration
const LEVEL_CONFIG = {
  1: { label: 'Débutant', color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-100' },
  2: { label: 'Basique', color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-100' },
  3: { label: 'Intermédiaire', color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-100' },
  4: { label: 'Avancé', color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-100' },
  5: { label: 'Expert', color: 'bg-green-500', textColor: 'text-green-600', bgLight: 'bg-green-100' },
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color, bgColor, onClick, trend, alert }) => (
  <Card 
    className={`cursor-pointer transition-all hover:shadow-md ${alert ? 'border-red-300 bg-red-50/50' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        {trend !== undefined && (
          <Badge variant="outline" className={trend >= 0 ? 'text-green-600' : 'text-red-600'}>
            {trend >= 0 ? '+' : ''}{trend}%
          </Badge>
        )}
        {alert && <AlertTriangle className="w-5 h-5 text-red-500" />}
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-3">{value}</p>
      <p className="text-sm text-slate-500">{title}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
);

// Global Status Badge
const GlobalStatusBadge = ({ data }) => {
  if (!data) return null;
  
  const { criticalGaps, avgScore, evaluatedEmployees, totalEmployees } = data;
  const coverage = totalEmployees > 0 ? (evaluatedEmployees / totalEmployees) * 100 : 0;
  
  if (criticalGaps > 5) {
    return (
      <Badge className="bg-red-100 text-red-700 px-3 py-1">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Critique ({criticalGaps} écarts)
      </Badge>
    );
  }
  
  if (coverage < 50) {
    return (
      <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
        <AlertCircle className="w-3 h-3 mr-1" />
        Couverture faible
      </Badge>
    );
  }
  
  if (avgScore >= 3.5) {
    return (
      <Badge className="bg-green-100 text-green-700 px-3 py-1">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Bon niveau
      </Badge>
    );
  }
  
  return (
    <Badge className="bg-blue-100 text-blue-700 px-3 py-1">
      <Activity className="w-3 h-3 mr-1" />
      En développement
    </Badge>
  );
};

// AI Insights Card for Skills
const AISkillsInsights = ({ data, isPro, onLaunchEvaluation, onCreatePlan }) => {
  const getInsights = () => {
    const insights = [];
    
    if (!data || data.totalSkills === 0) {
      insights.push({
        type: 'critical',
        icon: AlertTriangle,
        message: "Aucune compétence définie",
        detail: "Créez votre bibliothèque de compétences pour commencer à évaluer vos équipes.",
        action: "Créer des compétences"
      });
      return insights;
    }
    
    if (data.totalProfiles === 0) {
      insights.push({
        type: 'warning',
        icon: FileText,
        message: "Aucun profil de poste configuré",
        detail: "Les profils de poste permettent d'identifier les écarts de compétences par fonction.",
        action: "Créer un profil"
      });
    }
    
    if (data.criticalGaps > 0) {
      insights.push({
        type: 'critical',
        icon: AlertTriangle,
        message: `${data.criticalGaps} écart(s) critique(s) détecté(s)`,
        detail: "Des compétences essentielles manquent dans certaines équipes. Action recommandée.",
        action: "Voir les écarts"
      });
    }
    
    if (data.evaluatedEmployees < data.totalEmployees * 0.5) {
      insights.push({
        type: 'warning',
        icon: Users,
        message: `Seulement ${Math.round((data.evaluatedEmployees / data.totalEmployees) * 100)}% des employés évalués`,
        detail: "Lancez une campagne d'évaluation pour avoir une vision complète des compétences.",
        action: "Lancer évaluation"
      });
    }
    
    if (data.pendingPlans > 0) {
      insights.push({
        type: 'info',
        icon: GraduationCap,
        message: `${data.pendingPlans} plan(s) de développement en attente`,
        detail: "Des collaborateurs ont des plans d'upskilling à suivre.",
        action: "Voir les plans"
      });
    }
    
    if (data.lowSkillEmployees > 0) {
      insights.push({
        type: 'warning',
        icon: TrendingUp,
        message: `${data.lowSkillEmployees} employé(s) avec niveau faible`,
        detail: "Certains collaborateurs nécessitent un accompagnement renforcé.",
        action: "Créer plan upskilling"
      });
    }
    
    if (insights.length === 0) {
      insights.push({
        type: 'success',
        icon: CheckCircle2,
        message: "Gestion des compétences optimale",
        detail: "Vos équipes sont bien évaluées et les écarts sont sous contrôle.",
        action: null
      });
    }
    
    return insights;
  };
  
  const insights = getInsights();
  
  return (
    <Card className="bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50 border-purple-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            Synthèse IA - Compétences
          </CardTitle>
          <Badge className="bg-purple-100 text-purple-700">
            <Zap className="w-3 h-3 mr-1" />
            PRO
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {insights.map((insight, idx) => (
          <div 
            key={idx} 
            className={`p-3 rounded-lg border ${
              insight.type === 'critical' ? 'bg-red-50 border-red-200' :
              insight.type === 'warning' ? 'bg-amber-50 border-amber-200' :
              insight.type === 'success' ? 'bg-green-50 border-green-200' :
              'bg-blue-50 border-blue-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <insight.icon className={`w-5 h-5 mt-0.5 ${
                insight.type === 'critical' ? 'text-red-600' :
                insight.type === 'warning' ? 'text-amber-600' :
                insight.type === 'success' ? 'text-green-600' :
                'text-blue-600'
              }`} />
              <div className="flex-1">
                <p className="font-medium text-slate-900">{insight.message}</p>
                <p className="text-sm text-slate-600 mt-0.5">{insight.detail}</p>
              </div>
              {insight.action && (
                <Button size="sm" variant="outline" className="text-xs whitespace-nowrap">
                  {insight.action}
                </Button>
              )}
            </div>
          </div>
        ))}
        
        <div className="flex gap-2 pt-2">
          <Button 
            onClick={onLaunchEvaluation}
            className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            <Target className="w-4 h-4 mr-2" />
            Lancer évaluation
          </Button>
          <Button variant="outline" onClick={onCreatePlan} className="gap-2">
            <GraduationCap className="w-4 h-4" />
            Créer plan upskilling
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Skills Heatmap Component - Real data only
const SkillsHeatmap = ({ data, departments, hasData }) => {
  // Ne montrer que si des données réelles existent
  if (!hasData) {
    return (
      <Card className="border-dashed border-slate-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-slate-500">
            <BarChart3 className="w-5 h-5" />
            Heatmap Compétences × Équipes
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <PieChart className="w-8 h-8 text-slate-400" />
          </div>
          <p className="font-medium text-slate-700 mb-2">Visualisation non disponible</p>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Cette heatmap apparaîtra après avoir évalué vos équipes. 
            Suivez les étapes de configuration ci-dessous.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  // Real data rendering (when available)
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <BarChart3 className="w-5 h-5 text-purple-500" />
          Heatmap Compétences × Équipes
        </CardTitle>
        <CardDescription>Niveau moyen par compétence et département</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8 text-slate-500">
          Données en cours de chargement...
        </div>
      </CardContent>
    </Card>
  );
};

// Skills Distribution Chart - Real data only
const SkillsDistribution = ({ data, hasData }) => {
  if (!hasData) {
    return (
      <Card className="border-dashed border-slate-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-slate-500">
            <TrendingUp className="w-5 h-5" />
            Répartition par niveau
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-slate-400" />
          </div>
          <p className="font-medium text-slate-700 mb-2">Aucune évaluation</p>
          <p className="text-sm text-slate-500 max-w-xs mx-auto">
            La répartition des niveaux apparaîtra après les premières évaluations.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  const distribution = data?.levelDistribution || {};
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          Répartition par niveau
        </CardTitle>
        <CardDescription>Distribution des évaluations</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {Object.entries(LEVEL_CONFIG).map(([level, config]) => {
            const count = distribution[level] || 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            
            return (
              <div key={level} className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${config.color}`} />
                    <span className="font-medium text-slate-700">{config.label}</span>
                  </div>
                  <span className="text-slate-500">{count} ({pct}%)</span>
                </div>
                <Progress value={pct} className="h-2" />
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Score moyen</span>
            <span className="text-2xl font-bold text-purple-600">{data?.avgScore?.toFixed(1) || '-'}/5</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Setup Guide Component - Replaces fake data with actionable steps
const SetupGuide = ({ data, onNavigate }) => {
  const steps = [
    {
      id: 1,
      title: 'Créer vos compétences',
      description: 'Définissez la bibliothèque de compétences de votre organisation',
      icon: BookOpen,
      color: 'purple',
      completed: (data?.totalSkills || 0) > 0,
      count: data?.totalSkills || 0,
      action: () => onNavigate('/skills'),
      buttonText: 'Créer compétences'
    },
    {
      id: 2,
      title: 'Définir les profils de poste',
      description: 'Associez les compétences requises à chaque fonction',
      icon: Award,
      color: 'amber',
      completed: (data?.totalProfiles || 0) > 0,
      count: data?.totalProfiles || 0,
      action: () => onNavigate('/skills/profiles'),
      buttonText: 'Créer profils'
    },
    {
      id: 3,
      title: 'Évaluer vos équipes',
      description: 'Lancez des campagnes d\'évaluation des compétences',
      icon: Target,
      color: 'blue',
      completed: (data?.evaluatedEmployees || 0) > 0,
      count: data?.evaluatedEmployees || 0,
      action: () => onNavigate('/skills/evaluations'),
      buttonText: 'Lancer évaluation'
    },
    {
      id: 4,
      title: 'Plans de développement',
      description: 'Créez des parcours d\'upskilling personnalisés',
      icon: GraduationCap,
      color: 'green',
      completed: (data?.pendingPlans || 0) > 0,
      count: data?.pendingPlans || 0,
      action: () => onNavigate('/skills/upskilling'),
      buttonText: 'Créer plan'
    }
  ];
  
  const completedSteps = steps.filter(s => s.completed).length;
  const progress = (completedSteps / steps.length) * 100;
  
  const colorMap = {
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', ring: 'ring-purple-500' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', ring: 'ring-amber-500' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', ring: 'ring-blue-500' },
    green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200', ring: 'ring-green-500' },
  };
  
  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              Guide de configuration
            </CardTitle>
            <CardDescription className="mt-1">
              Suivez ces étapes pour activer votre module Compétences
            </CardDescription>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-purple-600">{completedSteps}/{steps.length}</p>
            <p className="text-xs text-slate-500">étapes complétées</p>
          </div>
        </div>
        <Progress value={progress} className="h-2 mt-4" />
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          {steps.map((step, idx) => {
            const colors = colorMap[step.color];
            const Icon = step.icon;
            const isNext = !step.completed && steps.slice(0, idx).every(s => s.completed);
            
            return (
              <div 
                key={step.id}
                className={`p-4 rounded-xl border-2 transition-all ${
                  step.completed 
                    ? 'bg-green-50 border-green-200' 
                    : isNext 
                      ? `bg-white ${colors.border} ring-2 ${colors.ring} ring-opacity-50` 
                      : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    step.completed ? 'bg-green-100' : colors.bg
                  }`}>
                    {step.completed ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${step.completed ? 'text-green-700' : 'text-slate-900'}`}>
                        {step.title}
                      </p>
                      {step.completed && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {step.count} créé{step.count > 1 ? 's' : ''}
                        </Badge>
                      )}
                      {isNext && (
                        <Badge className="bg-purple-100 text-purple-700 text-xs">
                          À faire
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{step.description}</p>
                    
                    {!step.completed && (
                      <Button 
                        size="sm" 
                        className={`mt-3 ${isNext ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                        variant={isNext ? 'default' : 'outline'}
                        onClick={step.action}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {step.buttonText}
                      </Button>
                    )}
                    
                    {step.completed && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="mt-3 text-green-600"
                        onClick={step.action}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Quick Actions Panel
const QuickActions = ({ onNavigate }) => (
  <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
    <CardHeader>
      <CardTitle className="text-lg">Actions rapides</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => onNavigate('/skills/evaluations')}>
          <Target className="w-6 h-6 text-purple-600" />
          <span className="text-sm">Lancer évaluation</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => onNavigate('/skills/upskilling')}>
          <GraduationCap className="w-6 h-6 text-blue-600" />
          <span className="text-sm">Plan upskilling</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => onNavigate('/skills')}>
          <BookOpen className="w-6 h-6 text-green-600" />
          <span className="text-sm">Bibliothèque</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => onNavigate('/skills/profiles')}>
          <Award className="w-6 h-6 text-amber-600" />
          <span className="text-sm">Profils de poste</span>
        </Button>
      </div>
    </CardContent>
  </Card>
);

export default function SkillsCockpitPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPro, isFree, isSuperAdmin, loading: planLoading } = usePlan();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  useEffect(() => {
    if (user?.companyId) loadData();
  }, [user?.companyId, selectedDepartment]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ company_id: user.companyId });
      if (selectedDepartment !== 'all') {
        params.append('department_id', selectedDepartment);
      }

      const [dashboardRes, deptRes, empRes, skillsRes, profilesRes] = await Promise.all([
        api.get(`/skills/dashboard/team?${params}`).catch(() => ({ data: {} })),
        api.get('/departments').catch(() => ({ data: [] })),
        api.get(`/employees?companyId=${user.companyId}`).catch(() => ({ data: [] })),
        api.get(`/skills/library?company_id=${user.companyId}`).catch(() => ({ data: { skills: [] } })),
        api.get(`/skills/job-profiles?company_id=${user.companyId}`).catch(() => ({ data: { profiles: [] } })),
      ]);

      const deptsList = deptRes.data.departments || deptRes.data || [];
      const empsList = empRes.data.employees || empRes.data || [];
      const skillsList = skillsRes.data.skills || skillsRes.data || [];
      const profilesList = profilesRes.data.profiles || profilesRes.data.jobProfiles || profilesRes.data || [];

      setDepartments(deptsList);
      setEmployees(empsList);

      // Use real data — fallback to local counts if dashboard endpoint missing
      const dash = dashboardRes.data || {};
      // Le backend retourne ses agrégats dans `summary` (totalEmployees, employeesEvaluated, etc.)
      const summary = dash.summary || {};
      setDashboardData({
        ...dash,
        ...summary,
        // "Compétences référencées" = la bibliothèque (skills library), pas seulement celles utilisées
        // dans des évaluations (qui correspond à summary.totalSkillsTracked)
        totalSkills: skillsList.length || summary.totalSkillsTracked || 0,
        totalEmployees: summary.totalEmployees ?? dash.totalEmployees ?? empsList.length,
        evaluatedEmployees: summary.employeesEvaluated ?? dash.evaluatedEmployees ?? 0,
        avgScore: summary.avgScore ?? dash.avgScore ?? 0,
        criticalGaps: summary.criticalGapsTotal ?? dash.criticalGaps ?? 0,
        pendingPlans: summary.activeDevelopmentPlans ?? dash.pendingPlans ?? 0,
        lowSkillEmployees: summary.employeesWithGaps ?? dash.lowSkillEmployees ?? 0,
        totalProfiles: dash.totalProfiles ?? profilesList.length,
      });

    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Redirect employees to their space
  if (user?.role === 'Employé') {
    navigate('/my-skills', { replace: true });
    return null;
  }

  if (loading || planLoading) {
    return (
      <DashboardLayout title="Compétences">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Compétences">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Brain className="h-7 w-7 text-purple-500" />
                Cockpit Compétences
              </h1>
              <GlobalStatusBadge data={dashboardData} />
            </div>
            <p className="text-slate-500 mt-1">
              Pilotez les compétences de vos équipes • {employees.length} collaborateurs
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les départements</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* KPI Cards - Real data only */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KPICard
            title="Compétences"
            value={dashboardData?.totalSkills || 0}
            icon={BookOpen}
            color="text-purple-600"
            bgColor="bg-purple-100"
            onClick={() => navigate('/skills')}
          />
          <KPICard
            title="Profils de poste"
            value={dashboardData?.totalProfiles || 0}
            icon={Award}
            color="text-amber-600"
            bgColor="bg-amber-100"
            onClick={() => navigate('/skills/profiles')}
          />
          <KPICard
            title="Employés évalués"
            value={`${dashboardData?.evaluatedEmployees || 0}/${dashboardData?.totalEmployees || 0}`}
            icon={Users}
            color="text-blue-600"
            bgColor="bg-blue-100"
            subtitle={dashboardData?.totalEmployees > 0 ? `${Math.round(((dashboardData?.evaluatedEmployees || 0) / dashboardData.totalEmployees) * 100)}% couverture` : 'Aucun employé'}
          />
          <KPICard
            title="Score moyen"
            value={dashboardData?.avgScore > 0 ? `${dashboardData.avgScore.toFixed(1)}/5` : '-'}
            icon={Gauge}
            color="text-green-600"
            bgColor="bg-green-100"
          />
          <KPICard
            title="Écarts critiques"
            value={dashboardData?.criticalGaps || 0}
            icon={AlertTriangle}
            color="text-red-600"
            bgColor="bg-red-100"
            alert={dashboardData?.criticalGaps > 0}
          />
          <KPICard
            title="Plans en cours"
            value={dashboardData?.pendingPlans || 0}
            icon={GraduationCap}
            color="text-indigo-600"
            bgColor="bg-indigo-100"
            onClick={() => navigate('/skills/upskilling')}
          />
        </div>

        {/* AI Insights */}
        <AISkillsInsights 
          data={dashboardData}
          isPro={isPro || isSuperAdmin}
          onLaunchEvaluation={() => navigate('/skills/evaluations')}
          onCreatePlan={() => navigate('/skills/upskilling')}
        />

        {/* Quick Actions */}
        <QuickActions onNavigate={navigate} />

        {/* Main Grid - Only show if data exists */}
        {(dashboardData?.evaluatedEmployees > 0 || dashboardData?.totalSkills > 5) && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Heatmap - takes 2 columns */}
            <div className="lg:col-span-2">
              <SkillsHeatmap data={dashboardData} departments={departments} hasData={dashboardData?.evaluatedEmployees > 0} />
            </div>
            
            {/* Distribution - 1 column */}
            <SkillsDistribution data={dashboardData} hasData={dashboardData?.evaluatedEmployees > 0} />
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
